// redux/slices/uploadChatDocument.js (WEB)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken, clearJwtToken } from "../../Utils/authStorage";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data ||
  error?.message ||
  fallback;

// Для FormData не проставляем Content-Type — axios сам поставит boundary
const getAuthHeaders = async ({ isFormData = false } = {}) => {
  const token = await getJwtToken();
  const base = isFormData ? {} : { "Content-Type": "application/json" };
  return token ? { ...base, Authorization: `Bearer ${token}` } : base;
};

/* ========================= THUNKS ========================= */

// ✅ Загрузка PDF-документа (JWT) — file это File из <input type="file">
export const uploadChatDocument = createAsyncThunk(
  "chatDocuments/uploadChatDocument",
  async ({ uid, messageId, file, onProgress }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("uid is required");
      if (!messageId) throw new Error("messageId is required");
      if (!file) throw new Error("file is required");

      const formData = new FormData();
      formData.append("uid", String(uid));
      formData.append("messageId", String(messageId));
      formData.append("document", file, file.name || "document.pdf");

      const serverUrl = getServerUrl();
      const headers = await getAuthHeaders({ isFormData: true });

      const response = await axios.post(`${serverUrl}/upload-document/upload`, formData, {
        headers,
        onUploadProgress: onProgress
          ? (e) => {
              const percent = Math.round((e.loaded * 100) / (e.total || 1));
              onProgress(percent);
            }
          : undefined,
      });

      return {
        url: `${serverUrl}${response.data.documentUrl}`,
        name: file.name,
        messageId,
      };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        await clearJwtToken().catch(() => {});
      }
      return rejectWithValue(getError(error, "uploadChatDocument failed"));
    }
  }
);

// ✅ Получение всех документов по uid (JWT)
export const fetchChatDocuments = createAsyncThunk(
  "chatDocuments/fetchChatDocuments",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("uid is required");

      const serverUrl = getServerUrl();
      const headers = await getAuthHeaders();

      const response = await axios.get(`${serverUrl}/upload-document/${uid}`, { headers });
      return (response.data || []).map((doc) => ({
        url: `${serverUrl}${doc.url}`,
        name: doc.name,
        messageId: doc.messageId,
      }));
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        await clearJwtToken().catch(() => {});
      }
      return rejectWithValue(getError(error, "fetchChatDocuments failed"));
    }
  }
);

/* ========================= STATE ========================= */

const initialState = {
  documents: [],
  status: "idle",
  error: null,
};

/* ========================= SLICE ========================= */

const chatDocumentSlice = createSlice({
  name: "chatDocuments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatDocuments.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchChatDocuments.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.documents = action.payload;
      })
      .addCase(fetchChatDocuments.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(uploadChatDocument.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadChatDocument.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.documents.push(action.payload);
      })
      .addCase(uploadChatDocument.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      .addCase(resetStore, () => initialState);
  },
});

export default chatDocumentSlice.reducer;